import type { SidebarEntry, SidebarValue } from "shared/constants";
import type { LocationQueryRaw } from "vue-router";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { SIDEBARS } from "shared/constants";

export interface SidebarStackEntry {
  sidebar: SidebarValue;
  params: Record<string, string>;
}

const STACK_SEPARATOR = ",";
const MAX_STACK_DEPTH = 5;

/**
 * Finds the SidebarEntry config from SIDEBARS that matches the given sidebar value.
 * Returns the paramKey and extraParamKeys so we know which query params belong to it.
 */
const findSidebarConfig = (
  sidebarValue: string,
): { paramKey?: string; extraParamKeys?: string[] } => {
  const entry = Object.values(SIDEBARS).find(
    (entry) => entry.sidebar === sidebarValue,
  );

  if (entry === undefined) {
    return {};
  }

  return {
    paramKey: "paramKey" in entry ? (entry.paramKey as string) : undefined,
    extraParamKeys:
      "extraParamKeys" in entry
        ? ([...entry.extraParamKeys] as string[])
        : undefined,
  };
};

/**
 * Gets all query param keys that belong to a specific sidebar entry.
 */
export const getParamKeysForSidebar = (sidebarValue: string): string[] => {
  const config = findSidebarConfig(sidebarValue);
  const keys: string[] = [];

  if (config.paramKey) {
    keys.push(config.paramKey);
  }

  if (config.extraParamKeys) {
    keys.push(...config.extraParamKeys);
  }

  return keys;
};

/**
 * Serializes a drawer stack into URL query params.
 * Sidebar names are comma-separated; each sidebar's params are merged flat.
 */
export const serializeStack = (
  stack: SidebarStackEntry[],
  preservedQuery: Record<string, string> = {},
): LocationQueryRaw => {
  if (stack.length === 0) {
    // Remove all sidebar-related params, keep non-sidebar query params
    const cleaned: Record<string, string> = {};

    for (const [key, value] of Object.entries(preservedQuery)) {
      if (key !== "sidebar" && !key.startsWith("sidebar-")) {
        cleaned[key] = value;
      }
    }

    return cleaned;
  }

  const query: LocationQueryRaw = {};

  // Preserve non-sidebar query params
  for (const [key, value] of Object.entries(preservedQuery)) {
    if (key !== "sidebar" && !key.startsWith("sidebar-")) {
      query[key] = value;
    }
  }

  // Comma-separated sidebar names (bottom → top)
  query.sidebar = stack.map((entry) => entry.sidebar).join(STACK_SEPARATOR);

  // Merge all sidebar params (each sidebar type has unique param keys)
  for (const entry of stack) {
    for (const [key, value] of Object.entries(entry.params)) {
      query[key] = value;
    }
  }

  return query;
};

/**
 * Deserializes URL query params back into a drawer stack.
 * Reads the comma-separated sidebar value and extracts each sidebar's params.
 */
export const deserializeStack = (
  query: Record<string, string | (string | null)[] | null | undefined>,
): SidebarStackEntry[] => {
  const sidebarParam = query.sidebar;

  if (
    sidebarParam === null ||
    sidebarParam === undefined ||
    typeof sidebarParam !== "string" ||
    sidebarParam === ""
  ) {
    return [];
  }

  const names = sidebarParam.split(STACK_SEPARATOR).filter(Boolean);

  return names.map((name) => {
    const paramKeys = getParamKeysForSidebar(name);
    const params: Record<string, string> = {};

    for (const key of paramKeys) {
      const value = query[key];

      if (typeof value === "string") {
        params[key] = value;
      }
    }

    return { sidebar: name as SidebarValue, params };
  });
};

/**
 * Builds a SidebarStackEntry from a sidebar config, entity ID, and extra params.
 */
const buildStackEntry = (
  sidebarConfig: SidebarEntry,
  id?: string | number,
  extraParams?: Record<string, string | number>,
): SidebarStackEntry => {
  const params: Record<string, string> = {};

  if ("paramKey" in sidebarConfig && id !== undefined) {
    params[sidebarConfig.paramKey] = String(id);
  }

  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      params[key] = String(value);
    }
  }

  return { sidebar: sidebarConfig.sidebar, params };
};

/**
 * Composable for managing a stack of drawers with URL synchronization.
 *
 * The stack is stored as a comma-separated `sidebar` query param in the URL.
 * Each sidebar's entity params (e.g. `sidebar-activity-id`) are stored as separate query params.
 */
export const useDrawerStack = () => {
  const router = useRouter();
  const route = useRoute();

  /**
   * Returns the current stack by reading from the URL.
   */
  const stack = computed(() =>
    deserializeStack(route.query as Record<string, string>),
  );

  /**
   * Returns the depth of the current stack.
   */
  const depth = computed(() => stack.value.length);

  /**
   * Returns the topmost sidebar value in the stack.
   */
  const topmost = computed(() =>
    stack.value.length > 0 ? stack.value[stack.value.length - 1].sidebar : null,
  );

  /**
   * Returns the preserved (non-sidebar) query params from the current route.
   */
  const getPreservedQuery = (): Record<string, string> => {
    const preserved: Record<string, string> = {};

    for (const [key, value] of Object.entries(route.query)) {
      if (typeof value === "string") {
        preserved[key] = value;
      }
    }

    return preserved;
  };

  /**
   * Pushes a new drawer onto the stack without closing existing ones.
   *
   * If the same sidebar type is already in the stack, no duplicate entry is
   * created: the existing entry gets the new params and is moved to the top of
   * the stack. For a drawer that already is the topmost that is an in-place
   * update — it re-fetches with the new entity ID. For a drawer lower in the
   * stack the move is what makes the push visible: a gesprek drawer under the
   * student drawer would otherwise swap its gesprek while staying hidden
   * behind the drawer the user is looking at.
   */
  const pushSidebar = (
    sidebarConfig: SidebarEntry,
    id?: string | number,
    extraParams?: Record<string, string | number>,
  ) => {
    const currentStack = stack.value;
    const entry = buildStackEntry(sidebarConfig, id, extraParams);

    const existingIndex = currentStack.findIndex(
      (e) => e.sidebar === sidebarConfig.sidebar,
    );

    if (existingIndex !== -1) {
      const newStack = [
        ...currentStack.filter((e) => e.sidebar !== sidebarConfig.sidebar),
        entry,
      ];

      const query = serializeStack(newStack, getPreservedQuery());

      return router.push({ query });
    }

    if (currentStack.length >= MAX_STACK_DEPTH) {
      console.warn(
        `[DrawerStack] Max stack depth (${MAX_STACK_DEPTH}) reached. Ignoring push.`,
      );
      return Promise.resolve();
    }

    const newStack = [...currentStack, entry];
    const query = serializeStack(newStack, getPreservedQuery());

    return router.push({ query });
  };

  /**
   * Replaces the entire stack with a single drawer (backwards-compatible with old openSidebar).
   */
  const replaceSidebar = (
    sidebarConfig: SidebarEntry,
    id?: string | number,
    extraParams?: Record<string, string | number>,
  ) => {
    const entry = buildStackEntry(sidebarConfig, id, extraParams);
    const query = serializeStack([entry], getPreservedQuery());

    return router.push({ query });
  };

  /**
   * Pops the topmost drawer from the stack, revealing the one below.
   */
  const popSidebar = () => {
    const currentStack = stack.value;

    if (currentStack.length === 0) {
      return Promise.resolve();
    }

    const newStack = currentStack.slice(0, -1);
    const query = serializeStack(newStack, getPreservedQuery());

    return router.push({ query });
  };

  /**
   * Closes all drawers in the stack.
   */
  const closeAllSidebars = () => {
    const query = serializeStack([], getPreservedQuery());

    return router.push({ query });
  };

  /**
   * Removes a specific sidebar and all sidebars above it from the stack.
   * Used when a drawer in the middle closes — everything above it must close too.
   */
  const closeSidebarAndAbove = (sidebarValue: SidebarValue) => {
    const currentStack = stack.value;
    const index = currentStack.findIndex(
      (entry) => entry.sidebar === sidebarValue,
    );

    if (index === -1) {
      return Promise.resolve();
    }

    const newStack = currentStack.slice(0, index);
    const query = serializeStack(newStack, getPreservedQuery());

    return router.push({ query });
  };

  /**
   * Checks if a given sidebar value is currently the topmost in the stack.
   */
  const isTopmost = (sidebarValue: SidebarValue): boolean => {
    return topmost.value === sidebarValue;
  };

  /**
   * Checks if a given sidebar value is in the stack.
   */
  const isInStack = (sidebarValue: SidebarValue): boolean => {
    return stack.value.some((entry) => entry.sidebar === sidebarValue);
  };

  /**
   * Returns the stack index (0-based) for a given sidebar value, or -1.
   */
  const getStackIndex = (sidebarValue: SidebarValue): number => {
    return stack.value.findIndex((entry) => entry.sidebar === sidebarValue);
  };

  return {
    stack,
    depth,
    topmost,
    pushSidebar,
    replaceSidebar,
    popSidebar,
    closeAllSidebars,
    closeSidebarAndAbove,
    isTopmost,
    isInStack,
    getStackIndex,
  };
};
